<template>
  <div data-ui="apply-buy-landing">
    <top-banner
      v-if="showTopBanner"
      v-bind="topBannerModel"
    />
    <fauxmo-message-banner
      v-else-if="showLimitedTimeOffer"
      v-bind="fauxmoModel"
    />
    <div class="apply-buy">
      <div class="apply-buy__col">
        <apply-buy-lead
          v-bind="applyBuyLeadModel"
          class="apply-buy__lead"
        />
      </div>

      <apply-buy-applet
        v-bind="appletModel"
        :application-enabled="applicationEnabled"
        :has-general-error="hasGeneralError"
        :has-household-error="hasHouseholdError"
        class="apply-buy__applet"
        @submit="handleSubmit"
      >
        <applet-accordion
          v-bind="signInLabelModel"
          @toggle="handleSignInAccordionToggle"
        >
          <sign-in v-bind="signInModel" />
        </applet-accordion>

        <applet-accordion
          v-bind="confirmMembershipLabelModel"
          @toggle="handleMembershipAccordionToggle"
        >
          <confirm-membership
            v-bind="confirmMembershipModel"
            @form-input="handleInput"
            @form-submit="startSubmitWithCaptcha"
            @blur="handleBlur"
          />
        </applet-accordion>

        <applet-accordion
          v-if="isEmployee"
          v-bind="employeeNumberLabelModel"
          @toggle="handleEmployeeAccordionToggle"
        >
          <confirm-employee-number
            v-bind="employeeNumberModel"
            :has-error="hasEmployeeNumberError || hasEmployeeNameError"
            :error-model="employeeErrorModel"
            @blur="handleEmployeeBlur"
            @form-input="handleEmployeeInput"
            @form-submit="submitEmployeeNumber"
          />
        </applet-accordion>
      </apply-buy-applet>

      <section class="apply-buy__terms">
        <div class="apply-buy__terms-inner">
          <terms-conditions
            v-if="showTermsConditions"
            v-bind="termsConditionsModel"
          />
          <capital-one-footer v-bind="footerModel" />
        </div>
      </section>
      <div class="apply-buy__captcha">
        <keep-alive v-if="enableCaptcha">
          <vue-recaptcha
            ref="recaptcha"
            :sitekey="captchaSiteKey"
            :load-recaptcha-script="true"
            badge="inline"
            class="member-lookup__recaptcha"
            size="invisible"
            @verify="doSubmitPostVerify"
          />
        </keep-alive>
      </div>
    </div>
  </div>
</template>

<script>
import { win } from '@rei/browser-shim';
import { getUser } from '@rei/session';
import { VueRecaptcha } from 'vue-recaptcha';
import { CdrBreakpointMd, CdrBreakpointSm } from '@rei/cdr-tokens';
import { useDynamicContent } from '@rei/cs-preview-fetch';

import { ValidationResultStatus } from '#validation/lib/index.mts';
import analyticsMixin from '#common/mixins/analyticsMixin';
import { useUserInformation } from '#common/composables/useUserInformation';
import CapitalOneFooter from '#components/capital-one-footer/CapitalOneFooter.vue';
import TopBanner from '#components/top-banner/TopBanner.vue';

import FauxmoMessageBanner from '#pages/mastercard/components/FauxmoMessageBanner.vue';
import { validateField } from '#utils/form-utils';
import {
  applyForCreditAsync,
  getConfirmMembershipFormData,
  handleGetUserResponse,
  spliceRetrievedUserData,
  submitConfirmMembershipFormAsync,
} from '#utils/apply-buy/apply-buy-utils';
import { getCartInformation } from '#utils/cartController';
import { getTopBannerModel } from '#utils/mastercard/mastercard-model-adapter';

import AppletAccordion from './components/AppletAccordion.vue';
import ApplyBuyLead from './components/ApplyBuyLead.vue';
import ApplyBuyApplet from './components/ApplyBuyApplet.vue';
import ConfirmMembership from './components/ConfirmMembership.vue';
import ConfirmEmployeeNumber from './components/ConfirmEmployeeNumber.vue';
import SignIn from './components/SignIn.vue';

import {
  CONFIRM_EMPLOYEE_INFO_FORM,
  CONFIRM_MEMBERSHIP_FORM,
  EMPLOYEE_NAME_MISMATCH_ERROR,
  EMPLOYEE_NUMBER_MISMATCH_ERROR,
} from './apply-buy-constants';

/**
 * Typescript specific imports
 */
import TermsConditions, { TermsConditionsContract } from '#components/terms-conditions/index.mts';

export default {
  name: 'ApplyBuyPage',
  components: {
    AppletAccordion,
    ApplyBuyApplet,
    ApplyBuyLead,
    CapitalOneFooter,
    ConfirmEmployeeNumber,
    ConfirmMembership,
    FauxmoMessageBanner,
    SignIn,
    TermsConditions,
    TopBanner,
    VueRecaptcha,
  },
  mixins: [
    analyticsMixin,
  ],
  props: {
    /* c8 ignore start */
    appProps: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    additionalData: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    componentModels: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    /* c8 ignore stop */
  },
  setup(props) {
    const getPreviewUrl = () => '/membership/mastercard/instant-credit/preview';
    const { content } = useDynamicContent(props, getPreviewUrl);
    const { isCardHolder, isMember } = useUserInformation({
      isCAASEnabled: props.appProps?.['cobrand.getCustomerAttributes.enabled'],
    });
    if (content.value?.componentModels) {
      content.value = content.value.componentModels;
    }
    // placeholder while TS conversion testing is underway
    const rawCms = { ...content };
    return {
      content,
      isCardHolder,
      isMember,
      rawCms,
    };
  },
  data() {
    return {
      isOpenEmployeeNumber: false,
      isOpenSignIn: true,
      isOpenMemberNumber: false,
      employeeNumberData: { ...CONFIRM_EMPLOYEE_INFO_FORM },
      fieldsData: [...CONFIRM_MEMBERSHIP_FORM],
      contact: null,
      isSignedIn: false,
      hasEnteredEmployeeNumber: false,
      hasEmployeeNumberError: false,
      hasEmployeeNameError: false,
      hasHouseholdError: false,
      hasGeneralError: false,
      hasMemberValidationResponseError: false,
      memberNumber: '',
      accountId: '', // updated via getUser
      screenWidth: 0, // mobile-first, baby
      windowUrlString: '',
      membershipAddedToCart: false,
    };
  },
  computed: {
    /* c8 ignore start */
    analyticsViewData() {
      const pageSection = this.isEmployee ? 'apply-employee' : 'apply';
      const { trackingId } = this;
      return {
        siteId: 'membership',
        templateType: 'mastercard',
        pageSection,
        ...(!!trackingId && { trackingId }),
      };
    },
    /* c8 ignore end */
    appletModel() {
      const { contact, isPreApproval } = this;

      return {
        isPreApproval,
        firstName: contact?.firstName,
      };
    },
    isMemberNonCardHolder() {
      return this.isMember && !this.isCardHolder;
    },
    showLimitedTimeOffer() {
      return this.featureToggles?.showLimitedTimeOffer;
    },
    fauxmoModel() {
      return {
        analyticsIdPage: this.isPreApproval ? 'preapproval' : 'instant-credit',
        isLimitedTimeOfferActive: this.showLimitedTimeOffer,
        limitedTimeOfferDate: this.frontendStrings?.limitedTimeOfferDate,
      };
    },
    applicationEnabled() {
      const {
        memberNumberPresent,
        isSignedIn,
        isEmployee,
        hasEnteredEmployeeNumber,
      } = this;
      if (isEmployee) {
        return memberNumberPresent
          && isSignedIn
          && hasEnteredEmployeeNumber;
      }
      return memberNumberPresent && isSignedIn;
    },
    applyBuyLeadModel() {
      const {
        content,
        frontendStrings,
        showDoubleRewardsPromo,
      } = this;
      const valueProps = content?.cardBenefits || [];
      return {
        ...frontendStrings,
        ...content?.lead,
        isDoubleRewardsActive: showDoubleRewardsPromo,
        valueProps,
      };
    },
    applyEndpoint() {
      return this.additionalData?.restEndPoints?.applyInstantCreditEndpoint;
    },
    preAppovalEndpoint() {
      return this.additionalData?.restEndPoints?.preApprovalInstantCreditEndpoint;
    },
    captchaSiteKey() {
      return this.additionalData?.CaptchaSiteKey;
    },
    confirmMembershipModel() {
      return {
        fields: this.fieldsData,
        hasMemberValidationResponseError: this.hasMemberValidationResponseError,
        isComplete: this.memberNumberPresent,
        isSignedIn: this.isSignedIn,
        membershipAddedToCart: this.membershipAddedToCart,
        isSmallScreen: this.isSmallScreen,
      };
    },
    confirmMembershipLabelModel() {
      const {
        isOpenMemberNumber: isOpen,
        memberNumberPresent: isComplete,
        contact,
      } = this;

      return {
        heading: 'Confirm your membership',
        isComplete,
        isOpen,
        sublabel: isComplete ? `Membership for <span>${contact?.firstName} ${contact?.lastName}</span> confirmed` : '',
        type: 'member-number',
      };
    },
    confirmMembershipEndpoint() {
      return this.additionalData?.restEndPoints.confirmMembershipEndpoint;
    },
    enableCaptcha() {
      return this.additionalData?.EnableCaptcha;
    },
    employeeNumberModel() {
      return {
        isComplete: this.hasEnteredEmployeeNumber,
        isSignedIn: this.isSignedIn,
        field: this.employeeNumberData,
      };
    },
    employeeErrorModel() {
      const { hasEmployeeNumberError, hasEmployeeNameError } = this;
      // 404 indicates a mismatch with employee number
      if (hasEmployeeNumberError) return EMPLOYEE_NUMBER_MISMATCH_ERROR;
      // 409 indicates a mismatch with employee name
      if (hasEmployeeNameError) return EMPLOYEE_NAME_MISMATCH_ERROR;
      // if there's no error, return empty obj (required prop)
      return {};
    },
    employeeNumberLabelModel() {
      const {
        isOpenEmployeeNumber: isOpen,
        hasEnteredEmployeeNumber: isComplete,
        employeeNumberData,
      } = this;

      return {
        isComplete,
        isOpen,
        heading: 'Confirm your employee number',
        sublabel: isComplete ? `Employee number <span>${employeeNumberData?.value}</span> added` : '',
        type: 'employee-number',
      };
    },
    featureToggles() {
      return this.appProps?.['feature.toggles'] || {};
    },
    footerModel() {
      return this.content?.mastercardFooter?.capOneFooter;
    },
    frontendStrings() {
      return this.appProps?.['frontend.strings'] || {};
    },
    isCobrandPreApprovalActive() {
      return this.appProps['cobrand.preapproval.enabled'];
    },
    isEmployee() {
      return this.additionalData?.isEmployee;
    },
    isSmallScreen() {
      return this.screenWidth < this.smallBreakpoint;
    },
    isPreApproval() {
      return this.isCobrandPreApprovalActive && this.additionalData?.isPreapproval;
    },
    smallBreakpoint() {
      return parseFloat(CdrBreakpointSm, 10);
    },
    mediumBreakpoint() {
      return parseFloat(CdrBreakpointMd, 10);
    },
    memberNumberPresent() {
      return !!this.memberNumber;
    },
    signInModel() {
      const {
        isEmployee, isPreApproval, isSignedIn,
      } = this;

      return {
        isPreApproval,
        isSignedIn,
        isEmployee,
      };
    },
    signInLabelModel() {
      const {
        isOpenSignIn: isOpen,
        isSignedIn: isComplete,
        contact,
      } = this;

      return {
        heading: 'Sign in to your online account',
        isComplete,
        isOpen,
        type: 'sign-in',
        sublabel: contact?.email ? `Signed in as <span>${contact.email}</span>` : '',
      };
    },
    showDoubleRewardsPromo() {
      return this.featureToggles?.showDoubleRewardsPromo;
    },
    trackingId() {
      return sessionStorage ? sessionStorage.getItem('cobrand.trackingId') : null;
    },
    topBannerModel() {
      return getTopBannerModel(this.content.topBanner);
    },
    showTopBanner() {
      return this.featureToggles?.allowTopBanner && this.content?.topBanner?.display;
    },
    /**
     * New Typescript-specific computed props
     */
    safeContent() {
      return {
        termsConditions: TermsConditionsContract.validate(this.content.mastercardFooter?.terms),
      };
    },
    showTermsConditions() {
      return this.safeContent.termsConditions.status === ValidationResultStatus.VALID;
    },
    termsConditionsModel() {
      const input = {
        ...this.safeContent.termsConditions.value,
      };
      // TODO: eliminate monkey-patching terms; use CMS for this
      if (this.showDoubleRewardsPromo && this.frontendStrings?.doubleRewardsDisclaimer) {
        input.showRetentionEventDisclaimer = true;
        input.retentionEventDisclaimer = `‡ ${this.frontendStrings?.doubleRewardsDisclaimer}`;
      }
      if (this.featureToggles?.showLimitedTimeOffer
        && this.frontendStrings?.limitedTimeOfferDisclaimer) {
        input.limitedTimeOfferActive = this.featureToggles.showLimitedTimeOffer;
        input.limitedTimeOfferDisclaimer = this.frontendStrings.limitedTimeOfferDisclaimer;
      }
      // END TODO
      return TermsConditionsContract.toProps(input);
    },
  },
  watch: {
    isSignedIn(nextValue) {
      if (nextValue) {
        const { memberNumber } = this;
        this.isOpenSignIn = false;
        this.isOpenMemberNumber = !memberNumber;
      }
    },
    memberNumber(nextValue) {
      if (nextValue !== '') {
        this.isOpenMemberNumber = false;
        if (this.isEmployee && !this.employeeNumberData.value) {
          this.isOpenEmployeeNumber = true;
        }
      }
    },
    // eslint-disable-next-line object-shorthand
    'employeeNumberData.value'(next, curr) {
      if (next !== curr && curr !== '') {
        this.hasEnteredEmployeeNumber = false;
        this.hasEmployeeNumberError = false;
        this.hasEmployeeNameError = false;
      }
    },
  },
  async created() {
    const { fieldsData, employeeNumberData } = this;
    if (typeof window !== 'undefined') {
      if (window.reiSession) {
        const { isMembershipAddedToCart } = await getCartInformation();
        this.membershipAddedToCart = isMembershipAddedToCart;

        getUser()
          .then(handleGetUserResponse)
          /*
           * handleGetUserResponse
           * includes: contact, isSignedIn, memberNumber, accountId
           */
          .then((response) => spliceRetrievedUserData({ response, fieldsData, employeeNumberData }))
          /*
           * spliceRetrievedUserData
           * can update fieldsData values with contact info
           */
          .then((nextState) => {
            Object.keys(nextState).forEach((key) => {
              this[key] = nextState[key];
            });
          })
          .catch(console.error);
      }

      /**
        Get 'trackingId' url param from window object.
        Note: This must occur in `created` because analyticsMixin run analytics init in mounted
              hook, which makes use of `trackingId`
      */
      if (window.location) {
        const urlParams = new URLSearchParams(window.location.search);
        const trackingId = urlParams.get('trackingId');
        const paidMediaTrackingId = urlParams.get('cm_mmc');
        if (trackingId) {
          window.newrelic?.setCustomAttribute('session-storage-enabled', this.isSessionStorageAvailable());
          sessionStorage.setItem('cobrand.trackingId', trackingId);
        } else if (paidMediaTrackingId) {
          window.newrelic?.setCustomAttribute('session-storage-enabled', this.isSessionStorageAvailable());
          sessionStorage.setItem('cobrand.trackingId', paidMediaTrackingId);
        }
        this.windowUrlString = window.location.href;
      }
    }
  },
  mounted() {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.setScreenWidth);
      this.setScreenWidth();
      if (sessionStorage.getItem('cobrand.referer') === 'undefined'
        && this.additionalData?.referer) {
        sessionStorage.setItem('cobrand.referer', this.additionalData?.referer);
      } else if (this.additionalData?.referer?.includes('checkout')) {
        sessionStorage.setItem('cobrand.referer', this.additionalData?.referer);
      }
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.setScreenWidth);
  },
  methods: {
    doSubmitPostVerify(recaptcha) {
      this.$refs.recaptcha.reset();
      const { fieldsData, confirmMembershipEndpoint: url } = this;
      const formData = getConfirmMembershipFormData(fieldsData);
      const payload = { url, recaptcha, ...formData };
      submitConfirmMembershipFormAsync(payload)
        .then((response) => {
          if (response.status === 404 || response.status === 400) {
            this.hasMemberValidationResponseError = true;
          } else {
            const { memberFirstName: firstName, memberLastName: lastName } = formData;
            this.contact = { firstName, lastName };
            this.memberNumber = formData.memberId;
          }
        })
        .catch(console.error);
    },
    handleSubmit(isPreApproval = false) {
      const pageTrackingId = sessionStorage ? sessionStorage.getItem('cobrand.trackingId') : null;
      const trackingReferer = sessionStorage ? sessionStorage.getItem('cobrand.trackingReferer') : null;
      const trackingEnabled = this.isSessionStorageAvailable();
      const referer = sessionStorage ? sessionStorage.getItem('cobrand.referer') : null;
      const empId = this.isEmployee ? this.employeeNumberData.value : null;
      const payload = {
        applyEndpoint: isPreApproval ? this.preAppovalEndpoint : this.applyEndpoint,
        empId,
        memberNumber: this.memberNumber,
        pageTrackingId,
        trackingReferer,
        trackingEnabled,
        requestURL: referer,
      };
      applyForCreditAsync(payload)
        .then((res) => res.json())
        .then(({
          errorCode, /* errorDescription, */ redirectURL, orchId, appId,
        }) => {
          const metricsData = {
            orchestratorId: orchId,
            applicationId: appId,
          };
          /* c8 ignore next */
          this.metricsLink({ linkName: 'Apply', ...(errorCode ? { errorCodes: errorCode } : {}), ...metricsData });

          if (redirectURL) {
            window.location.href = redirectURL;
          }

          if (errorCode) {
            // 401 indicates householded member
            if (errorCode === '401') {
              this.hasHouseholdError = true;
            // 404 indicates a mismatch with employee number
            // 409 indicates a mismatch with employee name
            } else if (errorCode === '404' || errorCode === '409') {
              this.isOpenEmployeeNumber = true;
              this.employeeNumberData.error = true;
              if (errorCode === '404') {
                this.hasEmployeeNumberError = true;
              } else {
                this.hasEmployeeNameError = true;
              }
            // generic error of some kind
            } else {
              this.hasGeneralError = true;
            }
            this.scrollToApplet();
          }
        })
        .catch(console.error);
    },
    handleBlur(index) {
      this.updateFields(index);
    },
    /* c8 ignore start */
    handleSignInAccordionToggle() {
      this.isOpenSignIn = !this.isOpenSignIn;
      if (this.signInLabelModel.isOpen) {
        this.metricsLink('sign-in-drawer_open');
      }
    },
    /* c8 ignore end */
    /* c8 ignore start */
    handleEmployeeAccordionToggle() {
      this.isOpenEmployeeNumber = !this.isOpenEmployeeNumber;
      if (win.metrics && this.employeeNumberLabelModel.isOpen) {
        this.metricsLink('confirm-employee-drawer_open');
      }
    },
    /* c8 ignore end */
    /* c8 ignore start */
    handleMembershipAccordionToggle() {
      this.isOpenMemberNumber = !this.isOpenMemberNumber;
      if (win.metrics && this.confirmMembershipLabelModel.isOpen) {
        this.metricsLink('confirm-member-drawer_open');
      }
    },
    /* c8 ignore end */
    /*
     * Input handler instead of v-model because we want errors to be corrected on keystroke
     * when they exist, not on blur
     */
    handleInput({ index, value }) {
      this.hasMemberValidationResponseError = false;
      this.fieldsData[index].value = value;
      /* field currently has error, see if that has been corrected */
      if (this.fieldsData[index].error) {
        this.updateFields(index);
      }
    },
    handleEmployeeBlur() {
      const { employeeNumberData } = this;
      const nextField = validateField(employeeNumberData);
      this.employeeNumberData = nextField;
    },
    handleEmployeeInput({ value }) {
      this.employeeNumberData.value = value;
      if (this.employeeNumberData.error) {
        this.handleEmployeeBlur();
      }
    },
    /* c8 ignore start */
    scrollToApplet() {
      const element = document.getElementById('applet');
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    },
    /* c8 ignore end */
    setScreenWidth() {
      const { innerWidth = 0 } = window;
      this.screenWidth = innerWidth;
    },
    startSubmitWithCaptcha() {
      this.hasMemberValidationResponseError = false;
      const validatedFields = this.fieldsData.map((field) => validateField(field));

      /* No errors across all fields, submit form payload */
      if (validatedFields.every(({ error }) => !error)) {
        /* c8 ignore start */
        if (win.metrics) {
          this.metricsLink('confirm-membership_submit');
        }
        this.$refs.recaptcha.execute();
        /* c8 ignore end */
      /* Errors were revealed, update fieldsData with error messages */
      } else {
        /* c8 ignore start */
        if (win.metrics) {
          const errorCodes = validatedFields.map((field) => field.error).join(',');
          this.metricsLink({ linkName: 'confirm-membership_submit', errorCodes });
        }
        /* c8 ignore end */
        this.fieldsData = validatedFields;
      }
    },
    isSessionStorageAvailable() {
      try {
        const value = 'test';
        const key = 'testKey';
        sessionStorage.setItem(key, value);
        const recoveredValue = sessionStorage.getItem(key);
        sessionStorage.removeItem(key);

        return recoveredValue === value;
      } catch (e) {
        return false;
      }
    },
    submitEmployeeNumber() {
      /* c8 ignore start */
      if (win.metrics) {
        this.metricsLink('confirm-employee_submit');
      }
      /* c8 ignore end */
      this.handleEmployeeBlur();
      if (!this.employeeNumberData.error) {
        this.hasEnteredEmployeeNumber = true;
        this.isOpenEmployeeNumber = false;
      }
    },
    /*
     * Accepts <Number> index of a specific field
     * Runs validation for that field and updates internal fieldsData state
     * to reflect validation outcome
     */
    updateFields(index) {
      const { fieldsData } = this;
      const field = fieldsData[index];
      const nextField = validateField(field);
      const nextFields = this.fieldsData.map((FIELD, i) => ((i === index) ? nextField : FIELD));
      this.fieldsData = nextFields;
    },
  },
};
</script>

<style lang="scss">
.apply-buy__col {
  @include cdr-md-mq-up {
    position: relative;
    background-color: $cdr-color-background-secondary;
  }
}

.apply-buy__terms {
  background-color: $cdr-color-background-secondary;
  padding-bottom: $cdr-space-one-x;
}

.apply-buy__applet {
  @include cdr-container;
  padding-top: 0;

  @include cdr-md-mq-up {
    max-width: 66.4rem;
  }
}

.apply-buy__captcha {
  padding-bottom: $cdr-space-one-and-a-half-x;
  background-color: $cdr-color-background-secondary;
  position: relative;
}

.apply-buy__terms-inner {
  @include cdr-container;
}

.applet__heading {
  @include cdr-md-mq-up {
    text-wrap: pretty;
  }
}

.apply-buy__captcha .member-lookup__recaptcha {
  @include cdr-md-mq-up {
    @include cdr-container;
  }
}

.terms {
  padding: $cdr-space-one-x 0;
}

@include cdr-md-mq-down {
  .apply-buy__captcha {
    @include cdr-container;
  }
}
</style>
